import type { MonthlyFmtTask } from '@cortex/ado-task-fmt';
import { businessColor } from './BusinessLabel';
import { PillSelect } from './PillSelect';

/**
 * FmtPartsCells の props。
 */
export interface FmtPartsCellsProps {
  /** 行の FMT 項目 */
  item: MonthlyFmtTask;
  /** FMT の全項目（選択肢を集める） */
  items: readonly MonthlyFmtTask[];
  /** 選び直したときに呼ばれる。区分を差し替えた項目を渡す */
  onChange: (item: MonthlyFmtTask) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 事業の選択肢を、FMT の全項目から重複なく名前順に集める
 * @graph-connects none
 */
function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)]
    .filter((value) => value !== '')
    .sort((a, b) => a.localeCompare(b, 'ja'));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT の一覧の事業・中分類の 2 列を、詳細と同じパステルの丸いプルダウンで出す。押しても行の編集は開かず、選び直すとその項目へ保存する。中分類の選択肢は同じ大分類のものだけ
 * @graph-connects PillSelect [calls] 丸いプルダウンで選ばせる
 * @graph-connects businessColor [calls] 事業の色を引く
 * @graph-connects uniqueSorted [calls] 選択肢を集める
 */
export function FmtPartsCells({ item, items, onChange }: FmtPartsCellsProps) {
  // 押したときに行の「編集を開く」が動かないよう、セルで止める
  const stop = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };
  return (
    <>
      <td className="py-2 pr-3 align-top text-[11px] font-semibold" onClick={stop}>
        <PillSelect
          label={`${item.title}の事業`}
          value={item.business}
          emptyLabel="事業なし"
          allowEmpty={false}
          options={uniqueSorted(items.map((entry) => entry.business))}
          color={businessColor(item.business).color}
          onChange={(business) => {
            onChange({ ...item, business });
          }}
        />
      </td>
      <td className="py-2 pr-3 align-top text-[11px] font-semibold" onClick={stop}>
        <PillSelect
          label={`${item.title}の中分類`}
          value={item.middleCategory}
          emptyLabel="中分類なし"
          options={uniqueSorted(
            items
              .filter((entry) => entry.majorCategory === item.majorCategory)
              .map((entry) => entry.middleCategory),
          )}
          color={item.middleCategory === '' ? undefined : 'var(--color-pill-middle)'}
          onChange={(middleCategory) => {
            onChange({ ...item, middleCategory });
          }}
        />
      </td>
    </>
  );
}
